import { getAppConfigSingleton } from "@expert/app-config";
import { useNotification } from "@expert/common-ui";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { type JSX, useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { shallow } from "zustand/shallow";

import type { Customer } from "@/customer-info";

import { useVerifiedCustomerStore } from "@/customer-info";
import { updateCase } from "@/customer-info/api/updateCase";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { isVoiceTask, useAgentSdk, useAgentStore, useCallbackState, usePartner, useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";

import { getBackupGaiaSummary, getGaiaSummary } from "../api/sessionSummaryApiClient";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { EditSessionSummaryModal } from "./EditSessionSummaryModal";
import styles from "./EditSessionSummaryModal.module.css";
import { adaptSimplifiedSummary } from "./utils";

/**
 * Stable empty customer object reference to prevent infinite loops from new references when using store selectors.
 */
const EMPTY_CUSTOMER: Partial<Customer> = {};

// TODO: Rename this component because it's not deprecated, and actually used by every partner except Verizon
export function DeprecatedSessionSummary(): JSX.Element | null {
    const logger = useLogger({ module: "SessionSummary", supportTeam: "solve" });
    const appConfig = getAppConfigSingleton();
    const activeSession = useSession();

    const { accountNumber, metadata: { caseNumber, caseId } = {} } = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(activeSession.id).customer ?? EMPTY_CUSTOMER,
    );
    const agentSdk = useAgentSdk();
    const partner = usePartner();
    const { accountNumberLength, wrappingAgentActivity, gaiaSummaryModel } = appConfig.getPartnersConfig(partner);
    const { simplifiedSummary } = usePartnerFeatureConfigured();
    const {
        features: { isAutoSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const validAccountNumber = simplifiedSummary
        ? !!caseId
        : accountNumberLength
          ? accountNumber?.length === accountNumberLength
          : false;

    const [setCanAppend, setSubmitted, autogeneratedSummary] = useSessionSummaryStore((state) => [
        state.setCanAppend,
        state.setSubmitted,
        state.autogeneratedSummary,
    ]);

    const [summaryLoaded, setSummaryLoaded] = useState(false);
    const [sendingSummary, setSendingSummary] = useState(false);

    const notification = useNotification();
    const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);

    const agentActivity = useAgentStore(useShallow((state) => state.activity));
    const callbackState = useCallbackState();

    const {
        summary,
        submitted,
        setSessionSummary,
        setSubject,
        subject,
        category,
        subcategory,
        setCategory,
        setSubcategory,
    } = useSessionSummaryStore((state) => state, shallow);
    const activeTask = activeSession.currentTask;

    const agentCallSid =
        activeTask && isVoiceTask(activeTask)
            ? (activeTask.agentCallId ??
              Array.from(activeSession.tasks.values())
                  .filter(isVoiceTask)
                  .findLast((task) => task.agentCallId !== undefined)?.agentCallId)
            : undefined;

    const shouldAutoGenerateSummary = activeTask?.locale !== "es-mx" && !!gaiaSummaryModel;

    const hasActiveCustomerSessionWrappingState =
        activeSession.kind === "with-customer" && !!activeSession.wrappingState;
    // A wrapping task or a newly wrapping agent means we are entering session summary
    const inWrappingState =
        activeTask?.status === "wrapping" ||
        agentActivity === wrappingAgentActivity ||
        hasActiveCustomerSessionWrappingState;

    const taskIsActive = activeTask?.status === "assigned";
    const hasImmediateCallBack = callbackState?.callbackType === "CallbackNow";

    const handleSimplifiedEndSession = async () => {
        try {
            if (!caseId) {
                await navigator.clipboard.writeText(summary);
                notification.success("Summary copied to clipboard");
                return;
            }
            setSendingSummary(true);
            await updateCase(logger, {
                caseId,
                subject,
                description: summary,
                category,
                subcategory,
            });
            notification.success("Summary sent");
            setSubmitted(true);
        } catch (error) {
            logger.warn({ error }, "DeprecatedSessionSummary | Error sending summary");
            notification.error("Error sending summary. Please copy the summary manually.");
        } finally {
            setSendingSummary(false);
        }
    };

    const setSessionSummaryFromGaia = useCallback(async () => {
        logger.trace(
            { agentCallSid, sessionId: activeSession.id, partner },
            "DeprecatedSessionSummary | Gaia summary fetch check",
        );
        const generatedSummary: string = agentCallSid
            ? await getGaiaSummary(
                  logger,
                  { callSid: agentCallSid, sessionId: activeSession.id, partner },
                  gaiaSummaryModel,
              )
            : await getBackupGaiaSummary(logger, { sessionId: activeSession.id, partner }, gaiaSummaryModel);

        if (!generatedSummary) {
            logger.warn("DeprecatedSessionSummary | Received empty summary from Gaia");
        } else if (simplifiedSummary) {
            const { subject, summary, category, subcategory } = adaptSimplifiedSummary(generatedSummary);
            setSessionSummary({ value: summary, autoGenerated: true });
            setSubject(subject);
            if (category) setCategory(category);
            if (subcategory) setSubcategory(subcategory);
        } else {
            setSessionSummary({ value: generatedSummary, autoGenerated: true });
        }
        setSummaryLoaded(true);
    }, [
        agentCallSid,
        activeSession.id,
        partner,
        setSessionSummary,
        setSummaryLoaded,
        gaiaSummaryModel,
        setSubject,
        simplifiedSummary,
        setSubcategory,
        setCategory,
        logger,
    ]);

    useEffect(() => {
        if (!shouldAutoGenerateSummary) {
            setSummaryLoaded(true);
            return;
        }

        if (opened && inWrappingState && !summaryLoaded) {
            if (autogeneratedSummary && !submitted && simplifiedSummary) {
                setSummaryLoaded(true);
                if (caseId) {
                    void handleSimplifiedEndSession();
                }
            } else if (!autogeneratedSummary || !summaryLoaded) {
                // NOTE: The summary loaded check is to prevent getting stuck in the loading state if we've already generated a summary from a previous leg of the call
                void setSessionSummaryFromGaia();
            }
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [inWrappingState, shouldAutoGenerateSummary, opened, autogeneratedSummary, submitted, simplifiedSummary]);

    useEffect(() => {
        if (hasImmediateCallBack) return;

        if (inWrappingState) {
            openModal();
        } else if (taskIsActive) {
            setCanAppend(true);
        }
    }, [hasImmediateCallBack, inWrappingState, taskIsActive, setCanAppend, openModal]);

    const shouldSendSummary = !submitted && summary;

    async function handleWrappingExpired() {
        await handleEndSession("WrappingTimeExpired");
    }

    const handleCopyAndSendSummary = useCallback(async () => {
        async function writeToClipboard() {
            try {
                await navigator.clipboard.writeText(summary);
            } catch (error) {
                logger.warn({ error }, "DeprecatedSessionSummary | Error copying session summary");
            }
        }
        await writeToClipboard();
    }, [logger, summary]);

    const handleEndSession = useCallback(
        async (reason: "AgentTerminatedWrapping" | "WrappingTimeExpired") => {
            if (validAccountNumber && isAutoSummaryEnabled) {
                logger.trace(
                    { isAutoSummaryEnabled, partner, reason, validAccountNumber },
                    "DeprecatedSessionSummary | Ending session wrapup",
                );
            } else await handleCopyAndSendSummary();
            logger.trace({ reason, partner }, "DeprecatedSessionSummary | Ending session wrapup");
            await agentSdk.endWrapup(reason, partner, activeSession.id, activeTask ?? undefined);
        },
        [
            activeSession.id,
            activeTask,
            agentSdk,
            handleCopyAndSendSummary,
            isAutoSummaryEnabled,
            logger,
            partner,
            validAccountNumber,
        ],
    );

    const sendSummary = useCallback(async () => {
        notification.success("Session summary copied to clipboard");
        try {
            await navigator.clipboard.writeText(summary);
        } catch (error) {
            logger.warn({ error }, "DeprecatedSessionSummary | Error copying session summary");
        }
    }, [logger, summary, notification]);

    const onDismiss = useCallback(
        async ({ checkSummary = true } = {}) => {
            logger.trace("DeprecatedSessionSummary | Session summary dismissed");
            closeModal();
            if (checkSummary && shouldSendSummary) {
                await sendSummary();
            }
        },
        [closeModal, logger, sendSummary, shouldSendSummary],
    );

    return (
        <Group className={styles.tempTimeline}>
            <EditSessionSummaryModal
                loading={!summaryLoaded && shouldAutoGenerateSummary}
                onDismiss={simplifiedSummary ? () => {} : () => void onDismiss()}
                sendingSummary={sendingSummary}
                onExpired={simplifiedSummary ? () => {} : handleWrappingExpired}
                simplifiedSummary={simplifiedSummary}
                caseNumber={caseNumber}
                validAccountNumber={validAccountNumber}
                endSession={simplifiedSummary ? handleSimplifiedEndSession : handleEndSession}
                opened={opened}
            />
        </Group>
    );
}
